(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;
  const summaryCache = new Map();

  function computeAverageScore(reviews) {
    if (!Array.isArray(reviews) || !reviews.length) {
      return 0;
    }

    const total = reviews.reduce((sum, review) => sum + (Number(review?.score) || 0), 0);
    return total / reviews.length;
  }

  async function fetchYotpoReviewSummary(appKey, productId) {
    const cacheKey = `${appKey}:${productId}`;

    if (summaryCache.has(cacheKey)) {
      return summaryCache.get(cacheKey);
    }

    const request = fetch(
      `https://api.yotpo.com/products/${encodeURIComponent(appKey)}/${encodeURIComponent(productId)}/reviews.json?page=1&per_page=50`
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Yotpo reviews failed: ${response.status}`);
        }

        const data = await response.json();
        const responseBody = data?.response || {};
        const reviews = Array.isArray(responseBody.reviews) ? responseBody.reviews : [];
        const bottomline = responseBody.bottomline || {};
        const ratingCount = Number(
          responseBody.total_reviews
          ?? bottomline.total_review
          ?? bottomline.totalReview
          ?? reviews.length
        ) || 0;
        const rating = Number(
          bottomline.average_score
          ?? bottomline.averageScore
          ?? computeAverageScore(reviews)
        ) || 0;

        return {
          rating,
          ratingCount,
        };
      })
      .catch(() => null);

    summaryCache.set(cacheKey, request);
    return request;
  }

  function formatCountLabel(template, count) {
    return String(template || '({{ count }})')
      .replace('__COUNT__', String(count))
      .replace('{{ count }}', String(count));
  }

  function formatRatingLabel(template, rating) {
    const safeRating = Math.max(0, Number(rating) || 0);
    const ratingText = safeRating.toFixed(1).replace(/\.0$/, '');

    return String(template || '{{ rating }} out of 5 stars')
      .replace('__RATING__', ratingText)
      .replace('{{ rating }}', ratingText);
  }

  function registerProductCardRating() {
    if (!root.document || !root.Alpine) {
      return;
    }

    root.Alpine.data('productCardRating', (config = {}) => ({
      appKey: config.appKey || root.theme?.yotpoAppKey || '',
      productId: String(config.productId || ''),
      rating: Number(config.rating) || 0,
      ratingCount: Number(config.ratingCount) || 0,
      reviewCountTemplate: config.reviewCountTemplate || '({{ count }})',
      ratingCountLabel: config.ratingCountLabel || '',
      ratingLabelTemplate: config.ratingLabelTemplate || '{{ rating }} out of 5 stars',
      verified: false,

      get showRating() {
        return this.verified && this.ratingCount > 0 && Number(this.rating) > 1;
      },

      get ratingAriaLabel() {
        return formatRatingLabel(this.ratingLabelTemplate, this.rating);
      },

      async init() {
        if (!this.ratingCountLabel) {
          this.ratingCountLabel = formatCountLabel(this.reviewCountTemplate, this.ratingCount);
        }

        if (!this.appKey || !this.productId || this.ratingCount <= 0) {
          this.verified = true;
          return;
        }

        const summary = await fetchYotpoReviewSummary(this.appKey, this.productId);

        if (summary) {
          this.rating = summary.rating;
          this.ratingCount = summary.ratingCount;
          this.ratingCountLabel = formatCountLabel(this.reviewCountTemplate, this.ratingCount);
          this.syncPreviewDataset();
        }

        this.verified = true;
      },

      syncPreviewDataset() {
        const card = this.$el?.closest('article');
        if (!card) {
          return;
        }

        const quickView = card.querySelector('[data-product-rating-count]');
        if (!quickView) {
          return;
        }

        quickView.dataset.productRating = String(this.rating || 0);
        quickView.dataset.productRatingCount = String(this.ratingCount || 0);
      },
    }));
  }

  if (root.document) {
    root.document.addEventListener('alpine:init', registerProductCardRating);

    if (root.Alpine) {
      registerProductCardRating();
    }
  }
})();
